import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { formatMoney } from './EstadoFinancieroReport';
import type { ConsumoPeriodo } from './api';

/**
 * "Consumo del Flujo Operativo" — la columna del estado leída como cascada:
 * lo que entró y en qué se fue, con el Margen Bruto y el Flujo Neto marcados
 * como líneas de referencia.
 *
 * Los números vienen calculados del backend (`buildConsumo`, sobre el mismo
 * reporte que arma la tabla), así que acá sólo hay dibujo. SVG a mano: son
 * cinco barras y dos líneas, no justifica sumar una librería de gráficos al
 * bundle.
 */

// Coordenadas del lienzo. El SVG escala con el ancho del diálogo; el viewBox
// mantiene las proporciones.
const W = 860;
const H = 340;
const M = { top: 20, right: 176, bottom: 72, left: 100 };
/** Ancho aproximado de un carácter a 10px — con esto se decide dónde cortar. */
const CHAR_W = 5.2;
const PLOT_W = W - M.left - M.right;
const PLOT_H = H - M.top - M.bottom;

export function ConsumoFlujoDialog({
  titulo,
  periodo,
  moneda,
  onClose,
}: {
  titulo: string | null;
  periodo: ConsumoPeriodo | undefined;
  moneda: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!titulo && !!periodo} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(56rem,95vw)] max-w-none">
        <DialogHeader>
          <DialogTitle className="pr-8">
            {`Consumo del Flujo Operativo — ${titulo ?? ''}`}
          </DialogTitle>
        </DialogHeader>
        {periodo && <Grafico periodo={periodo} moneda={moneda} />}
      </DialogContent>
    </Dialog>
  );
}

function Grafico({ periodo, moneda }: { periodo: ConsumoPeriodo; moneda: string }) {
  const { barras, margenBruto, flujoNeto, base } = periodo;

  // El dominio incluye siempre el 0: las barras salen de la línea de base, y
  // sin el 0 adentro una columna chica se dibujaría del alto del panel.
  const valores = [0, ...barras.map((b) => b.valor), margenBruto, flujoNeto];
  let max = Math.max(...valores);
  let min = Math.min(...valores);
  if (max === min) max = min + 1; // período vacío: eje degenerado
  const pad = (max - min) * 0.07;
  max += pad;
  min -= pad;

  const y = (v: number) => M.top + ((max - v) / (max - min)) * PLOT_H;
  const y0 = y(0);
  const slot = PLOT_W / barras.length;
  const barW = Math.min(slot * 0.5, 70);

  const ticks = Array.from({ length: 5 }, (_, i) => min + ((max - min) * i) / 4);

  // Margen Bruto y Flujo Neto coinciden en cualquier mes sin costos indirectos
  // ni impuestos — que es lo habitual. Se separan las etiquetas para que la de
  // abajo no tape a la de arriba.
  const yMargen = y(margenBruto);
  let yFlujo = y(flujoNeto);
  if (Math.abs(yFlujo - yMargen) < 26) yFlujo = yMargen + 26;

  const pct = (v: number) =>
    base ? `${Math.abs((v / base) * 100).toFixed(1).replace('.', ',')}% del Flujo Op.` : '';

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label="Consumo del flujo operativo del período"
    >
      {ticks.map((t) => (
        <g key={t}>
          <line
            x1={M.left}
            x2={M.left + PLOT_W}
            y1={y(t)}
            y2={y(t)}
            className="stroke-border"
            strokeWidth={1}
          />
          <text
            x={M.left - 10}
            y={y(t) + 4}
            textAnchor="end"
            className="fill-muted-foreground text-[11px]"
          >
            {formatMoney(t, moneda)}
          </text>
        </g>
      ))}

      {/* Base en 0, más marcada que la grilla: es de donde nacen las barras. */}
      <line
        x1={M.left}
        x2={M.left + PLOT_W}
        y1={y0}
        y2={y0}
        className="stroke-muted-foreground"
        strokeWidth={1.5}
      />

      {barras.map((b, i) => {
        const x = M.left + slot * i + (slot - barW) / 2;
        const top = Math.min(y0, y(b.valor));
        const alto = Math.abs(y0 - y(b.valor));
        return (
          <g key={b.label}>
            {b.valor !== 0 && (
              <rect
                x={x}
                y={top}
                width={barW}
                height={Math.max(alto, 1)}
                className={b.valor < 0 ? 'fill-red-500' : 'fill-emerald-600'}
              >
                <title>{`${b.label}: ${formatMoney(b.valor, moneda)}`}</title>
              </rect>
            )}
            <EtiquetaEje x={M.left + slot * i + slot / 2} label={b.label} slot={slot} />
          </g>
        );
      })}

      <Referencia
        y={yMargen}
        yLinea={y(margenBruto)}
        color="text-sky-600"
        label="Margen bruto de caja"
        detalle={`${formatMoney(margenBruto, moneda)} · ${pct(margenBruto)}`}
      />
      <Referencia
        y={yFlujo}
        yLinea={y(flujoNeto)}
        color="text-emerald-600"
        label="Flujo neto del período"
        detalle={`${formatMoney(flujoNeto, moneda)} · ${pct(flujoNeto)}`}
      />
    </svg>
  );
}

/** Línea de referencia punteada con su rótulo en el margen derecho. */
function Referencia({
  y,
  yLinea,
  color,
  label,
  detalle,
}: {
  /** Alto del rótulo — puede estar corrido para no solaparse con el otro. */
  y: number;
  yLinea: number;
  color: string;
  label: string;
  detalle: string;
}) {
  return (
    <g className={color}>
      <line
        x1={M.left}
        x2={M.left + PLOT_W}
        y1={yLinea}
        y2={yLinea}
        stroke="currentColor"
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
      <text x={M.left + PLOT_W + 8} y={y - 2} fill="currentColor" className="text-[11px]">
        {label}
      </text>
      <text x={M.left + PLOT_W + 8} y={y + 11} fill="currentColor" className="text-[10px] opacity-80">
        {detalle}
      </text>
    </g>
  );
}

/**
 * Rótulo de una barra. Los Ítems del EF tienen nombres largos ("IMPUESTOS
 * DEFINITIVOS / LIQUIDACIONES") y la columna mide unos 85px con siete barras,
 * así que se reparte en renglones según el ancho real disponible. El nombre
 * completo queda en el tooltip.
 */
function EtiquetaEje({ x, label, slot }: { x: number; label: string; slot: number }) {
  const lineas = partirEtiqueta(label, Math.max(6, Math.floor((slot - 6) / CHAR_W)));
  return (
    <text x={x} textAnchor="middle" className="fill-muted-foreground text-[10px]">
      <title>{label}</title>
      {lineas.map((l, i) => (
        <tspan key={i} x={x} y={M.top + PLOT_H + 18 + i * 12}>
          {l}
        </tspan>
      ))}
    </text>
  );
}

/**
 * Reparte en renglones de a lo sumo `maxChars`, cortando por palabra. Más de
 * `maxLineas` empuja el gráfico hacia arriba, así que a partir de ahí se
 * elide — el `<title>` conserva el nombre entero.
 */
export function partirEtiqueta(label: string, maxChars: number, maxLineas = 3): string[] {
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of label.split(/\s+/)) {
    const cand = actual ? `${actual} ${palabra}` : palabra;
    // `!actual`: una palabra sola más larga que el ancho igual arranca la
    // línea — se recorta abajo, pero nunca deja el renglón vacío.
    if (cand.length <= maxChars || !actual) actual = cand;
    else {
      lineas.push(actual);
      actual = palabra;
    }
  }
  if (actual) lineas.push(actual);

  const cortadas = lineas.slice(0, maxLineas);
  if (lineas.length > maxLineas) cortadas[maxLineas - 1] += '…';
  return cortadas.map((l) => (l.length > maxChars ? `${l.slice(0, maxChars - 1)}…` : l));
}
